import { z } from "zod";

import { ArchivedView, ListContentFilter, ListOwnership } from "@/enum/list.enum";

/**
 * Zod schema for the Lead List overview filters a "pinned" tab restores.
 *
 * A pinned tab captures the reusable filter combination — the structured filters (scope + content
 * + archived + source) **and** an optional free-text search term. When a search term is pinned the
 * `ownership` field holds the *search scope* (the Search Results tab's own scope axis), since that
 * is the scope actually applied while searching.
 */
export const PinnedTabFiltersSchema = z.object({
	/**
	 * Scope the tab restores (My / Team / All). When {@link search} is set this is the Search
	 * Results scope; otherwise it is the ownership-tab scope.
	 */
	ownership: z.nativeEnum(ListOwnership),
	/** "Show" content filter. */
	contentFilter: z.nativeEnum(ListContentFilter),
	/** Archived visibility. */
	archivedView: z.nativeEnum(ArchivedView),
	/** Normalised source type — `undefined` means "any source". */
	sourceType: z.string().optional(),
	/** Free-text search term — `undefined`/empty means "no search". */
	search: z.string().optional(),
});

export type PinnedTabFilters = z.infer<typeof PinnedTabFiltersSchema>;

/** A user-named pinned filter tab, persisted per-user on the backend. */
export interface PinnedFilterTab {
	id: number;
	name: string;
	filters: PinnedTabFilters;
}

/**
 * Whether the given (already-normalised) filters are worth pinning. A tab is only worth saving
 * when at least one *secondary* filter is non-default — either a free-text search term, or a
 * non-default content/archived/source filter. The bare ownership scopes are excluded: they are
 * already reachable through the built-in My/Team/All tabs, so an ownership change alone is not
 * pinnable.
 */
export const isFilterPinnable = (filters: PinnedTabFilters): boolean =>
	!!filters.search?.trim() ||
	filters.contentFilter !== ListContentFilter.ALL ||
	filters.archivedView !== ArchivedView.HIDE ||
	filters.sourceType !== undefined;

/** Structural equality of two filter snapshots. */
export const arePinnedFiltersEqual = (a: PinnedTabFilters, b: PinnedTabFilters): boolean =>
	a.ownership === b.ownership &&
	a.contentFilter === b.contentFilter &&
	a.archivedView === b.archivedView &&
	(a.sourceType ?? undefined) === (b.sourceType ?? undefined) &&
	(a.search?.trim() || undefined) === (b.search?.trim() || undefined);

/**
 * The id of the pinned tab whose snapshot exactly matches the current filters, or `undefined`
 * when none match. Used to light the active pinned tab as the user adjusts filters.
 */
export const findMatchingPinnedTabId = (
	tabs: PinnedFilterTab[],
	filters: PinnedTabFilters
): number | undefined => tabs.find((tab) => arePinnedFiltersEqual(tab.filters, filters))?.id;
